import { OutOfPageAdType, PageAdSize, PageAdType, isOutOfPageAdType } from '@/modules/adv'
import { loadScript } from '@/utils/loadScript'

import { type HeaderBiddingConfig, PrebidGlobal } from './types'

export class HeaderBidding {
    config: HeaderBiddingConfig

    isInitialized = false

    constructor(config: HeaderBiddingConfig) {
        this.config = config
        this.init()
    }

    private async init(): Promise<void> {
        try {
            if (!window.pbjs) {
                window.pbjs = { que: [] } as unknown as PrebidGlobal
            }

            await loadScript({
                src: '/prebid10.9.0.js',
                useExisting: true,
            })

            if (!window.pbjs || !window.pbjs.setConfig) {
                throw new Error('Prebid.js failed to load properly')
            }

            window.pbjs.que.push(() => {
                window.pbjs?.setConfig({
                    priceGranularity: 'dense',
                    bidderTimeout: this.config?.timeout || 2000,
                    enableSendAllBids: true,
                })
                this.isInitialized = true
            })
        } catch {
            this.isInitialized = false
        }
    }

    private getAdUnit(
        code: string,
        context: {
            type: PageAdType | OutOfPageAdType
            sizes?: PageAdSize[]
        },
    ) {
        const mediaTypes = isOutOfPageAdType(context.type)
            ? {
                  video: {
                      context: 'instream',
                  },
              }
            : {
                  banner: {
                      sizes: context.sizes?.filter((s) => s !== 'fluid'),
                  },
              }

        return {
            code,
            mediaTypes,
            bids: this.config?.bidders,
        }
    }

    public runAuction(
        adUnit: string,
        context: {
            type: PageAdType | OutOfPageAdType
            sizes?: PageAdSize[]
        },
    ): Promise<Record<string, string[]> | null> {
        if (!window.pbjs || !this.config) {
            return Promise.resolve(null)
        }

        const timeoutMs = this.config?.timeout || 2000

        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                resolve(null)
            }, timeoutMs)

            window.pbjs?.que.push(() => {
                window.pbjs?.addAdUnits([this.getAdUnit(adUnit, context)])
                window.pbjs?.requestBids({
                    timeout: timeoutMs,
                    bidsBackHandler: () => {
                        clearTimeout(timeout)
                        resolve(window.pbjs?.getAdserverTargetingForAdUnitCode(adUnit) || null)
                    },
                })
            })
        })
    }
}
